import { useQuery } from "@tanstack/react-query";
import { useBusinesses } from "@/hooks/use-businesses";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, RefreshCw, Clock, AlertCircle } from "lucide-react";

// v2 — force cache bust
const fmt = (n: number | string | null | undefined) =>
  (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

function timeAgo(date: string | null | undefined): string {
  if (!date) return "Never synced";
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

interface BankSummaryCardProps {
  pendingCount?: number;
}

export function BankSummaryCard({ pendingCount = 0 }: BankSummaryCardProps) {
  const { activeBusiness } = useBusinesses();
  const businessId = activeBusiness?.id;

  const { data: accounts, isLoading } = useQuery<any[]>({
    queryKey: ["/api/plaid-accounts", businessId],
    queryFn: async () => {
      try {
        const r = await fetch(`/api/businesses/${businessId}/plaid/linked-accounts`, { credentials: "include" });
        if (!r.ok) return [];
        const data = await r.json();
        return Array.isArray(data) ? data.filter((a: any) => a.isSelected !== false) : [];
      } catch {
        return [];
      }
    },
    enabled: !!businessId,
    staleTime: 60000,
  });

  // Fetch lastSyncedAt from connection-status
  const { data: connectionStatus } = useQuery<any>({
    queryKey: [`/api/businesses/${businessId}/plaid/connection-status`],
    queryFn: async () => {
      try {
        const r = await fetch(`/api/businesses/${businessId}/plaid/connection-status`, { credentials: "include" });
        if (!r.ok) return null;
        return r.json();
      } catch { return null; }
    },
    enabled: !!businessId,
    staleTime: 30000,
  });
  const lastSynced = connectionStatus?.lastSyncedAt;

  if (isLoading) {
    return (
      <div className="flex gap-4 mb-6 overflow-x-auto pb-1">
        {[1, 2].map(i => <Skeleton key={i} className="h-[88px] min-w-[260px] flex-1 rounded-lg" />)}
      </div>
    );
  }

  if (!accounts || accounts.length === 0) return null;

  return (
    <div className="flex gap-3 mb-6 overflow-x-auto pb-1">
      {accounts.map((acct: any) => (
        <div
          key={acct.id || acct.accountId}
          className="flex-shrink-0 min-w-[240px] flex-1 max-w-[320px] border rounded-lg px-4 py-3 bg-card"
        >
          {/* Row 1: Bank name + last 4 */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm font-medium truncate">{acct.name || "Bank Account"}</span>
            </div>
            {acct.mask && (
              <span className="text-xs text-muted-foreground font-mono ml-2 shrink-0">
                ****{acct.mask}
              </span>
            )}
          </div>
          {/* Row 2: Balance */}
          <div className="text-lg font-semibold tabular-nums mb-1.5">
            {fmt(acct.balanceCurrent ?? acct.balance_current)}
          </div>
          {/* Row 3: Sync time + pending */}
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3" />
              Synced {timeAgo(acct.lastSyncedAt || lastSynced || acct.last_synced_at)}
            </span>
            {acct.subtype && (
              <span className="capitalize">{acct.subtype}</span>
            )}
          </div>
        </div>
      ))}

      {/* Pending transactions summary */}
      {pendingCount > 0 && (
        <div className="flex-shrink-0 min-w-[200px] border border-yellow-200 rounded-lg px-4 py-3 bg-yellow-50/50">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-800">Needs Attention</span>
          </div>
          <div className="text-lg font-semibold text-yellow-700">{pendingCount}</div>
          <div className="text-[11px] text-yellow-600">pending transactions</div>
        </div>
      )}
    </div>
  );
}
